import React, { Fragment } from "react";
import { Button, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import usePut from "../../../../../../../../hooks/usePut";
import { PRICES_BASE_REQUEST } from "../../../../../../../../enums/ApiPaths";
import { SERVICES } from "../../../../../../../../enums/QueryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useGetWarehousePreferences } from "../../../hooks/useGetWarehousePreferences";
import { parseDecimal, deriveTotalPrice } from "../../../utils/decimalInput";
import ServicePriceFields from "./ServicePriceFields";

// The "Pricing" chip tab in ServiceChipsTab - own small form, own PUT, same
// shape as the sibling Other.jsx/ServiceTitleForm.jsx tabs, reusing
// ServicePriceFields for the actual inputs (shared with the add-service
// form) so the netto/brutto/decimal logic isn't duplicated.
export default function ServicePricingForm({ price, clinicId }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const editPath = PRICES_BASE_REQUEST + clinicId;
  const edit = usePut(editPath, SERVICES + clinicId);
  const { data: warehousePreferences } = useGetWarehousePreferences(clinicId);
  const priceEntryMode = warehousePreferences?.priceEntryMode ?? "NETTO";
  const currency = warehousePreferences?.currency ?? "EUR";

  const storedPrice = price?.price;
  const storedPdv = price?.pdv;
  const initialPriceField = React.useMemo(() => {
    if (storedPrice == null) return "";
    if (priceEntryMode === "BRUTTO" && storedPdv != null) {
      return (Number(storedPrice) * (1 + Number(storedPdv) / 100)).toFixed(2);
    }
    return String(storedPrice);
    // Only recompute from the stored value on mount / when the record
    // changes - not on every priceEntryMode flip, matching
    // WarehouseItemForm's one-time BRUTTO edit-seeding behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price?.id]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      price: initialPriceField,
      pdv: storedPdv != null ? String(storedPdv) : "",
    },
  });

  const priceValue = watch("price");
  const pdvValue = watch("pdv");
  const derivedPrice = React.useMemo(
    () => deriveTotalPrice(priceValue, pdvValue, priceEntryMode),
    [priceValue, pdvValue, priceEntryMode],
  );

  const submitData = async (data) => {
    const enteredPrice = parseDecimal(data.price);
    const pdv = parseDecimal(data.pdv);
    // Blank price/pdv are sent as null and, per DataInterface.update's
    // null-skip merge, leave whatever pricing already exists untouched -
    // this form can add or change a price but can't clear one back to
    // "no price", the same limitation every other partial-edit tab here
    // already has for its own fields.
    const netPrice =
      priceEntryMode === "BRUTTO" && enteredPrice != null && pdv != null
        ? Number((enteredPrice / (1 + pdv / 100)).toFixed(2))
        : enteredPrice;

    const obj = { ...price, price: netPrice, pdv };
    const res = await edit.submitCallback(obj);
    if (res) {
      queryClient.invalidateQueries(SERVICES + clinicId);
    }
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit(submitData)}>
        <Stack spacing={2}>
          <ServicePriceFields
            control={control}
            errors={errors}
            priceEntryMode={priceEntryMode}
            derivedPrice={derivedPrice}
            currency={currency}
          />
          <Stack direction="row" alignItems="center" spacing={2} mt={2}>
            <Button variant="contained" color="info" type="submit">
              {t("Save")}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Fragment>
  );
}
